/**
 * `/v1/areas` and `/v1/areas/:id` in local mode (area taxonomy plan §2.2).
 *
 * Industry membership is a stable round-robin over the live per-sector list
 * AFTER sorting tickers. The assignment ignores the index filter so the same
 * ticker cannot change industry when the caller asks for DJI.
 *
 * `unclassifiedCount` is ALWAYS 0 here, and that is a property of the
 * assignment rather than of the fixture: every sector has a non-empty live
 * industry list, so the round-robin classifies every row. The HTTP path
 * reports a real count from rows whose `areaId` is null. A local test pinning
 * 0 is therefore asserting the invariant, not observing the data.
 */
import {
  SECTOR_IDS,
  type AreaDetail,
  type AreaId,
  type AreaListPayload,
  type AreaQuery,
  type WindowKey,
} from '@meridian/contracts';

import { buildCorrelationMatrix } from '../../analytics/correlation-matrix';
import { aggregateGroups, toAreaAggregate } from '../../analytics/group-aggregate';
import { sectorDiversifiers } from '../../analytics/sector-diversifiers';
import { COLLATOR } from '../../constants/collation';
import { SECTOR_DIVERSIFIER_PANEL } from '../../constants/diversification';
import {
  INDUSTRIES_BY_SECTOR,
  isLiveIndustryId,
  liveIndustryName,
} from '../../constants/industries';
import { AREA_DETAIL_MIN_MEMBERS, MATRIX_SIZE, MOVERS_SIZE } from '../../constants/panels';
import { DatasetNotFoundError } from '../../ports/dataset-repository';
import { emptiedFilterId, type BookOwnership } from '../../selectors/filter-book';
import { narrowUniverse, unknownCompareTicker } from '../../selectors/filter-compare';
import { topByReturn } from '../../selectors/rankings';
import { sortRows } from '../../selectors/sort-rows';
import { toWireSecurity, type DerivedSecurity } from '../../universe/derive-security';

/**
 * How many of a sector's industries the round-robin actually uses.
 *
 * Spreading a sector across its FULL catalog list put ~1.2 names in each
 * industry, so no local industry ever reached AREA_DETAIL_MIN_MEMBERS and
 * `getArea` could never emit `matrix` or a non-empty `diversifiers` — while
 * HTTP emits both routinely. That is a shape-parity break (plan §4.7 ·
 * §1.1 rule 5) the dual-mode E2E cannot catch, because the local side never
 * produces the panel to compare.
 *
 * Sizing the list by member count instead guarantees >= AREA_DETAIL_MIN_MEMBERS
 * per industry whenever the sector has that many names. It stays a pure
 * deterministic function of the sorted peer list and remains independent of
 * the index filter. The below-threshold branch stays reachable through index
 * filters, which shrink an area's members without changing its assignment.
 */
function industriesUsed(list: readonly AreaId[], peerCount: number): readonly AreaId[] {
  return list.slice(0, Math.max(1, Math.floor(peerCount / AREA_DETAIL_MIN_MEMBERS)));
}

export function assignIndustries(rows: readonly DerivedSecurity[]): ReadonlyMap<string, AreaId> {
  return new Map(
    SECTOR_IDS.flatMap((sc) => {
      const peers = rows
        .filter((r) => r.sc === sc)
        .map((r) => r.ticker)
        .sort((a, b) => COLLATOR.compare(a, b));
      const list = industriesUsed(INDUSTRIES_BY_SECTOR[sc], peers.length);
      return peers.flatMap((ticker, i) => {
        const id = list[i % list.length];
        return id === undefined ? [] : [[ticker, id]];
      });
    }),
  );
}

interface ClassifiedMember extends DerivedSecurity {
  readonly areaId: AreaId;
}

/**
 * The two precomputations the repository injects, bundled rather than trailing
 * as positional parameters. `assigned` alone was a fourth argument; adding
 * `owned` beside it made five, over the style §3 limit — and a call site reading
 * `(rows, id, query, assigned, owned)` is one transposition away from a silent
 * wrong answer. Both stay optional so direct callers (tests) are unchanged.
 */
export interface LocalAreaContext {
  /** Industry assignment, computed once at repository construction. */
  readonly assigned?: ReadonlyMap<string, AreaId>;
  /** Manual-book ownership; omitted means the book filter is off. */
  readonly owned?: BookOwnership;
}

function requireCompare(rows: readonly DerivedSecurity[], query: AreaQuery): void {
  const unknown = unknownCompareTicker(new Set(rows.map((row) => row.ticker)), query.compare);
  if (unknown !== undefined) throw new DatasetNotFoundError('security', unknown);
}

export function buildAreaList(
  rows: readonly DerivedSecurity[],
  query: AreaQuery,
  context: LocalAreaContext = {},
): AreaListPayload {
  requireCompare(rows, query);
  const assigned = context.assigned ?? assignIndustries(rows);
  const pool = narrowUniverse(rows, query.index, query.compare, context.owned);
  const classified: readonly ClassifiedMember[] = pool.flatMap((row) => {
    const id = assigned.get(row.ticker);
    return id === undefined ? [] : [{ ...row, areaId: id }];
  });
  const areas = aggregateGroups(classified, {
    idOf: (row) => row.areaId,
    nameOf: liveIndustryName,
    window: query.window,
  }).map(toAreaAggregate);
  return { areas: [...areas], unclassifiedCount: pool.length - classified.length };
}

function areaMembers(
  rows: readonly DerivedSecurity[],
  id: AreaId,
  query: AreaQuery,
  context: Required<Pick<LocalAreaContext, 'assigned'>> & LocalAreaContext,
): readonly DerivedSecurity[] {
  return narrowUniverse(rows, query.index, query.compare, context.owned).filter(
    (r) => context.assigned.get(r.ticker) === id,
  );
}

function areaPanels(
  members: readonly DerivedSecurity[],
  others: readonly DerivedSecurity[],
  window: WindowKey,
): Pick<AreaDetail, 'matrix' | 'diversifiers'> {
  if (members.length < AREA_DETAIL_MIN_MEMBERS) return { diversifiers: [] };
  const byMcap = sortRows(members, { k: 'mcap', d: -1 }, { window });
  return {
    matrix: buildCorrelationMatrix(byMcap.slice(0, MATRIX_SIZE), window),
    diversifiers: [...sectorDiversifiers(members, others, SECTOR_DIVERSIFIER_PANEL)],
  };
}

export function buildAreaDetail(
  rows: readonly DerivedSecurity[],
  id: AreaId,
  query: AreaQuery,
  context: LocalAreaContext = {},
): AreaDetail {
  if (!isLiveIndustryId(id)) throw new DatasetNotFoundError('area', id);
  requireCompare(rows, query);
  const assigned = context.assigned ?? assignIndustries(rows);
  const members = areaMembers(rows, id, query, { ...context, assigned });
  if (members.length === 0) {
    throw new DatasetNotFoundError(
      'area',
      emptiedFilterId(id, query.index, query.book, query.compare),
    );
  }
  const [aggregate] = aggregateGroups(members, {
    idOf: () => id,
    nameOf: liveIndustryName,
    window: query.window,
  });
  if (aggregate === undefined) {
    throw new DatasetNotFoundError('area', `${id} (no aggregate)`);
  }
  return {
    area: { id, name: liveIndustryName(id) },
    aggregate: toAreaAggregate(aggregate),
    members: members.map(toWireSecurity),
    movers: topByReturn(members, query.window, MOVERS_SIZE).map(toWireSecurity),
    ...areaPanels(
      members,
      rows.filter((r) => assigned.get(r.ticker) !== id),
      query.window,
    ),
  };
}
